/**
 * ---------------------------------------------------------------------------------------------------------------------
 * CARD SLIDESHOW
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Cycles the media of a product card. A single scroll-snap track backs every input: arrows on a fine pointer, a swipe
 * on a coarse one, and an optional timer while the pointer rests on the card.
 *
 * The arrows sit outside the card's anchor, in the shared figure, so that clicking one cannot open the product.
 *
 * Attributes:
 *   interval: milliseconds between two slides (defaults to 1200)
 *   autoplay: present when hovering should advance the images on its own
 */
class CardSlideshow extends HTMLElement {
  #timer = null;
  #index = 0;

  connectedCallback() {
    this.track = this.querySelector('.card-slideshow__track');
    this.slides = Array.from(this.querySelectorAll('.card-slideshow__slide'));
    this.dots = Array.from(this.querySelectorAll('.card-slideshow__dot'));

    if (!this.track || this.slides.length < 2) {
      return;
    }

    this.interval = parseInt(this.getAttribute('interval'), 10) || 1200;
    this.autoplay = this.hasAttribute('autoplay');
    this.canHover = window.matchMedia('(pointer: fine)').matches;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Hover is tracked on the figure, not on this element: the arrows sit outside it, and leaving for one of them
    // would otherwise read as leaving the card and snap the images back to the first one.
    this.figure = this.closest('.product-card__figure') || this;

    if (this.canHover) {
      this.figure.addEventListener('pointerenter', this.#onPointerEnter);
      this.figure.addEventListener('pointerleave', this.#onPointerLeave);
      this.#connectArrows();
    } else {
      // The visitor drives the track, so the dots only have to follow it
      this.track.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#guardClickAfterSwipe();
    }

    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #onPointerEnter = () => {
    this.classList.add('card-slideshow--active');

    if (!this.autoplay || this.reducedMotion) {
      return;
    }

    this.#stop();
    this.#timer = setInterval(() => this.#step(1), this.interval);
  };

  #onPointerLeave = () => {
    this.classList.remove('card-slideshow--active');
    this.#stop();
    this.#goTo(0, 'auto');
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
    }
  };

  #onScroll = () => {
    const index = Math.round(this.track.scrollLeft / this.track.clientWidth);

    if (index !== this.#index) {
      this.#index = index;
      this.#paintDots();
    }
  };

  /**
   * Wires the arrows rendered next to this element inside the shared figure. Using one arrow stops the timer: once the
   * visitor takes over, the images should not keep moving under their cursor.
   */
  #connectArrows() {
    const arrows = this.figure.querySelectorAll('.card-slideshow__arrow');

    arrows.forEach((arrow) => {
      arrow.addEventListener('click', (event) => {
        event.preventDefault();
        this.#stop();
        this.#step(arrow.dataset.direction === 'prev' ? -1 : 1);
      });
    });
  }

  #step(offset) {
    const count = this.slides.length;

    this.#goTo((this.#index + offset + count) % count);
  }

  /**
   * A horizontal drag on a card is a swipe, not a tap on the link that wraps it, so the click it ends with is dropped.
   */
  #guardClickAfterSwipe() {
    let startX = null;
    let swiped = false;

    this.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
      swiped = false;
    }, { passive: true });

    this.addEventListener('pointermove', (event) => {
      if (startX !== null && Math.abs(event.clientX - startX) > 10) {
        swiped = true;
      }
    }, { passive: true });

    this.addEventListener('click', (event) => {
      if (swiped) {
        event.preventDefault();
        event.stopPropagation();
        swiped = false;
      }
    }, true);
  }

  #goTo(index, behavior) {
    this.#index = index;
    this.track.scrollTo({
      left: this.track.clientWidth * index,
      behavior: behavior || (this.reducedMotion ? 'auto' : 'smooth')
    });
    this.#paintDots();
  }

  #paintDots() {
    this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === this.#index ? 'true' : 'false'));
  }

  #stop() {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('card-slideshow')) {
  customElements.define('card-slideshow', CardSlideshow);
}
